import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2,
  MessageCircle,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRound,
  Users,
  X,
} from "lucide-react";

import {
  ChatConversation,
  ChatMember,
  chatApi,
} from "../api/chat";
import { Cohort, programApi } from "../api/program";
import ChatDrawer from "../components/Chat/ChatDrawer";
import { useAppSelector } from "../hooks/reducer";

type ChatChannel = {
  id: string;
  label: string;
  description: string;
  member?: ChatMember;
};

const memberName = (member?: Partial<ChatMember> | null) =>
  member?.username?.trim() || member?.email?.trim() || "Користувач";

const conversationPreview = (conversation: ChatConversation) => {
  const lastMessage = conversation.lastMessage;
  if (!lastMessage) return "Немає повідомлень";
  if (lastMessage.deletedAt) return "Повідомлення видалено";
  return lastMessage.text || lastMessage.attachments?.[0]?.name || "Вкладення";
};

const ChatPage = () => {
  const user = useAppSelector((state) => state.authReducer.data?.user);
  const isAdmin = Boolean(user?.isAdmin || user?.role === "admin");
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [cohortId, setCohortId] = useState(user?.cohortId || "");
  const [cohort, setCohort] = useState<Cohort | null>(null);
  const [members, setMembers] = useState<ChatMember[]>([]);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedChannel, setSelectedChannel] = useState("general");
  const [search, setSearch] = useState("");
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);
  const [mobileView, setMobileView] = useState<"list" | "chat">("list");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadChatData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      let selectedCohort: Cohort;
      let selectedId = cohortId;

      if (isAdmin) {
        const availableCohorts = cohorts.length
          ? cohorts
          : await programApi.listCohorts();
        if (!cohorts.length) setCohorts(availableCohorts);
        selectedId = selectedId || availableCohorts[0]?._id || "";
        if (!selectedId) {
          setCohort(null);
          setMembers([]);
          setConversations([]);
          return;
        }
        if (!cohortId) setCohortId(selectedId);
        const calendar = await programApi.getCohortCalendar(selectedId);
        selectedCohort = calendar.cohort;
      } else {
        const calendar = await programApi.getMyCalendar();
        selectedCohort = calendar.cohort;
        selectedId = calendar.cohort._id;
        if (cohortId !== selectedId) setCohortId(selectedId);
      }

      const [chatMembers, existingConversations] = await Promise.all([
        chatApi.listMembers(selectedId),
        chatApi.listConversations(selectedId),
      ]);
      setCohort(selectedCohort);
      setMembers(chatMembers);
      setConversations(existingConversations);
    } catch (requestError: any) {
      setError(
        requestError.response?.data?.message ||
          "Не вдалося завантажити чати."
      );
    } finally {
      setLoading(false);
    }
  }, [cohortId, cohorts, isAdmin]);

  useEffect(() => {
    loadChatData();
  }, [loadChatData]);

  useEffect(() => {
    setSelectedChannel("general");
    setSearch("");
    setMemberPickerOpen(false);
    setMobileView("list");
  }, [cohortId]);

  const channels = useMemo<ChatChannel[]>(() => {
    const privateChats = conversations
      .filter((conversation) => conversation.partner?._id)
      .map((conversation) => ({
        id: conversation.partner._id,
        label: memberName(conversation.partner),
        description: conversationPreview(conversation),
        member: conversation.partner,
      }));

    return [
      {
        id: "general",
        label: "Загальний чат потоку",
        description: "Повідомлення бачать усі",
      },
      ...privateChats,
    ];
  }, [conversations]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("uk-UA");
    return members.filter(
      (member) => {
        const name = memberName(member).toLocaleLowerCase("uk-UA");
        const email = member.email?.toLocaleLowerCase("uk-UA") || "";
        return !query || name.includes(query) || email.includes(query);
      }
    );
  }, [members, search]);

  const activeChannel =
    channels.find((channel) => channel.id === selectedChannel) ||
    (selectedChannel === "general"
      ? channels[0]
      : {
          id: selectedChannel,
          label: "Приватний діалог",
          description: "",
          member: members.find((member) => member._id === selectedChannel),
        });

  return (
    <section
      className={`${
        mobileView === "chat"
          ? "h-full overflow-hidden p-0 lg:px-8 lg:py-7"
          : "flex h-full min-h-0 flex-col overflow-hidden px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-7"
      }`}
    >
      <div
        className={`mx-auto w-full max-w-[1450px] ${
          mobileView === "chat" ? "h-full lg:h-auto" : "flex min-h-0 flex-1 flex-col"
        }`}
      >
        <div
          className={`mb-5 flex-col gap-4 md:flex-row md:items-end md:justify-between ${
            mobileView === "chat" ? "hidden lg:flex" : "flex"
          }`}
        >
          <div>
            <p className="text-sm font-black uppercase tracking-[0.14em] text-[#8a6f00]">
              {isAdmin ? "Адміністрування" : "Моя програма"}
            </p>
            <h2 className="mt-1 text-3xl font-black tracking-[-0.04em] text-black">
              Чати спільноти
            </h2>
            <p className="mt-2 text-sm text-[#817A72]">
              Загальний чат потоку та приватні повідомлення.
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
            {isAdmin && (
              <select
                value={cohortId}
                onChange={(event) => setCohortId(event.target.value)}
                className="h-11 w-full rounded-2xl border-2 border-black/10 bg-white px-3 text-sm font-bold outline-none focus:border-black focus:ring-4 focus:ring-[#ffd400]/30 md:w-72"
              >
                {cohorts.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name} · {item.code}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={loadChatData}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-black text-black transition hover:bg-[#fff8cf]"
            >
              <RefreshCw size={16} />
              Оновити
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-2xl border-2 border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex h-[60vh] items-center justify-center gap-2 rounded-[28px] border-2 border-black/10 bg-white text-[#817A72]">
            <Loader2 size={19} className="animate-spin" />
            Завантажуємо чати...
          </div>
        ) : !cohort ? (
          <div className="rounded-[28px] border-2 border-dashed border-black/20 bg-white p-12 text-center text-[#817A72]">
            Немає доступного потоку для чату.
          </div>
        ) : (
          <div
            className={`min-h-0 overflow-hidden bg-white lg:grid lg:h-[calc(100dvh-220px)] lg:min-h-[620px] lg:grid-cols-[320px_minmax(0,1fr)] lg:rounded-[28px] lg:border-2 lg:border-black/10 lg:shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${
              mobileView === "chat"
                ? "h-full"
                : "min-h-0 flex-1 rounded-[28px] border-2 border-black/10 shadow-[0_18px_50px_rgba(15,23,42,0.08)]"
            }`}
          >
            <aside
              className={`h-full min-h-0 flex-col border-b border-black/10 bg-white lg:flex lg:border-b-0 lg:border-r-2 ${
                mobileView === "chat" ? "hidden" : "flex"
              }`}
            >
              <div className="border-b-2 border-black/10 bg-[#fffdf2] p-4">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black">
                    <Users size={19} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-black text-black">
                      {cohort.name}
                    </p>
                    <p className="text-xs text-[#817A72]">
                      {conversations.length} приватних діалогів
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMemberPickerOpen(true)}
                  className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5"
                >
                  <Plus size={17} />
                  Нове повідомлення
                </button>
              </div>

              <div className="max-h-56 overflow-y-auto p-2 lg:max-h-none lg:flex-1">
                {channels.map((channel) => (
                  <button
                    key={channel.id}
                    type="button"
                    onClick={() => {
                      setSelectedChannel(channel.id);
                      setMobileView("chat");
                    }}
                    className={`mb-1 flex w-full items-center gap-3 rounded-xl p-3 text-left transition ${
                      activeChannel.id === channel.id
                        ? "bg-[#fff8cf] text-black"
                        : "text-[#514B45] hover:bg-[#fffdf2]"
                    }`}
                  >
                    <div
                      className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${
                        activeChannel.id === channel.id
                          ? "border-2 border-black bg-[#ffd400] text-black"
                          : "bg-[#EEEAE4] text-[#716A62]"
                      }`}
                    >
                      {channel.member ? (
                        channel.member.isAdmin ||
                        channel.member.role === "admin" ? (
                          <ShieldCheck size={17} />
                        ) : (
                          <UserRound size={17} />
                        )
                      ) : (
                        <MessageCircle size={17} />
                      )}
                    </div>
                    <div className="min-w-0">
                    <p className="truncate text-sm font-black">
                        {channel.label}
                      </p>
                      <p className="truncate text-xs opacity-70">
                        {channel.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </aside>

            <div
              className={`h-full min-h-0 ${
                mobileView === "list" ? "hidden lg:block" : "block"
              }`}
            >
              <ChatDrawer
                key={`${cohort._id}-${activeChannel.id}`}
                open
                embedded
                onClose={() => undefined}
                cohortId={cohort._id}
                cohortName={cohort.name}
                recipientId={activeChannel.member?._id || null}
                recipientName={activeChannel.member?.username}
                onMobileBack={() => setMobileView("list")}
                onMessageSent={async () => {
                  const updated = await chatApi.listConversations(cohort._id);
                  setConversations(updated);
                }}
              />
            </div>
          </div>
        )}
      </div>

      {memberPickerOpen && cohort && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/35 p-3">
          <button
            type="button"
            className="absolute inset-0"
            onClick={() => setMemberPickerOpen(false)}
            aria-label="Закрити вибір учасника"
          />
          <div className="relative flex max-h-[75vh] w-full max-w-md flex-col overflow-hidden rounded-[28px] border-2 border-black bg-white shadow-[10px_10px_0_#ffd400]">
            <div className="flex items-center justify-between border-b-2 border-black bg-[#fffdf2] p-4">
              <div>
                <p className="font-black text-black">
                  Нове повідомлення
                </p>
                <p className="text-xs text-[#817A72]">
                  Оберіть учасника потоку
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMemberPickerOpen(false)}
                className="grid h-9 w-9 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black"
              >
                <X size={17} />
              </button>
            </div>
            <div className="p-3">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a6f00]"
                />
                <input
                  autoFocus
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Знайти людину"
                  className="h-11 w-full rounded-2xl border-2 border-black/10 bg-white pl-9 pr-3 text-sm font-semibold outline-none focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
              {filteredMembers.map((member) => (
                <button
                  key={member._id}
                  type="button"
                  onClick={() => {
                    setSelectedChannel(member._id);
                    setMobileView("chat");
                    setMemberPickerOpen(false);
                    setSearch("");
                  }}
                  className="flex w-full items-center gap-3 rounded-2xl p-3 text-left hover:bg-[#fff8cf]"
                >
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-black/10 bg-[#fff8cf] text-black">
                    {member.isAdmin || member.role === "admin" ? (
                      <ShieldCheck size={18} />
                    ) : (
                      <UserRound size={18} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-black text-black">
                    {memberName(member)}
                    </p>
                    <p className="truncate text-xs text-[#817A72]">
                      {member.isAdmin || member.role === "admin"
                        ? "Адміністратор"
                        : "Учасник потоку"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ChatPage;
